'use client';

import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import { CalendarClock, Check, ChevronDown, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatNextRun } from '@/lib/routines/format-next-run';

interface ScheduleEditorProps {
  /** Natural-language phrase — the PRIMARY input and the source of truth. */
  scheduleNatural: string;
  onScheduleNaturalChange: (value: string) => void;
  /** Derived cron the scheduler fires on. Auto-filled from the phrase; editable
   *  in the advanced section for precise control. */
  scheduleCron: string;
  onScheduleCronChange: (value: string) => void;
  timezone: string;
  onTimezoneChange: (value: string) => void;
}

// 'deferred': the live parser was unavailable (rate limit / network) — NOT a
// user error. The phrase still becomes the real schedule at save time
// (resolveScheduleCron preferNatural), so the copy promises exactly that.
type ParseState = 'idle' | 'parsing' | 'valid' | 'invalid' | 'deferred';

/**
 * The schedule step, made human-first. The user types when the routine should run
 * in plain language ("every weekday at 9am"); we parse it to a validated cron via
 * /api/routines/parse-schedule (Claude, gated by cron-parser) and show a live
 * confirmation of exactly what will fire. The raw cron lives behind an "Advanced"
 * disclosure for power users — editing it there re-describes locally (no LLM).
 *
 * Both the create wizard and the edit form use this, so the two surfaces can't
 * drift. It owns only the parse UX; the three values stay controlled by the parent.
 */
export function ScheduleEditor({
  scheduleNatural,
  onScheduleNaturalChange,
  scheduleCron,
  onScheduleCronChange,
  timezone,
  onTimezoneChange,
}: ScheduleEditorProps) {
  const [state, setState] = useState<ParseState>(scheduleCron.trim() ? 'valid' : 'idle');
  const [description, setDescription] = useState<string | null>(null);
  // Next concrete occurrence (ISO) of the current cron in the current timezone
  // — the line that turns "runs at 09:10 am, monday through friday" into a
  // checkable promise ("Next run: Mon, Jul 20, 8:30 AM").
  const [nextRun, setNextRun] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Skip parsing on the initial render for values the parent pre-filled (edit
  // form): only a real user edit should spend an LLM call. Flipped true the first
  // time the corresponding field's onChange fires.
  const naturalTouched = useRef(false);
  const cronTouched = useRef(false);

  // Supersession guard for describe fetches: only the LATEST request may apply
  // its response. Without it, a slow early response (mount, or a debounced
  // advanced-cron edit) can land AFTER a newer parse and overwrite the line
  // with a stale schedule/instant. Bumped by every new describe, by effect
  // cleanup (unmount / re-run), and by a successful natural-language parse.
  const describeSeq = useRef(0);
  // Always-current timezone for in-flight/debounced describes — a debounce
  // armed before a TimezoneSelect change must not fetch with the old tz (the
  // response would render an old-tz instant under the new tz label).
  const timezoneRef = useRef(timezone);
  timezoneRef.current = timezone;

  // Describe an existing cron (no LLM), shared by the mount/tz-change effect
  // and the advanced-cron edit. MUST send the timezone: the description text
  // is tz-agnostic, but nextRun is an instant — omitting the tz would compute
  // it in UTC and show a skewed "Next run" line.
  const describeCronRemote = async (cron: string) => {
    const seq = ++describeSeq.current;
    try {
      const res = await fetch('/api/routines/parse-schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cron, timezone: timezoneRef.current }),
      });
      const data = await res.json();
      if (describeSeq.current !== seq) return; // superseded — drop
      if (data.valid) {
        setDescription(data.description);
        setNextRun(typeof data.nextRun === 'string' ? data.nextRun : null);
        setState('valid');
        setError(null);
      } else {
        setDescription(null);
        setNextRun(null);
        setState('invalid');
        setError(data.error || 'That cron expression isn’t valid.');
      }
    } catch {
      /* keep prior state — leave the raw cron visible */
    }
  };

  // Mount + timezone change, one effect: on mount (state initializes 'valid'
  // for a pre-filled cron) this paints the confirmation + "Next run" line; on
  // a tz change the rule text stays the same but the next-run INSTANT moves
  // (9 AM New York ≠ 9 AM UTC) — re-describe with the new tz. A single effect
  // so the edit form doesn't fire two identical requests on mount.
  useEffect(() => {
    const cron = scheduleCron.trim();
    if (!cron || state !== 'valid') return;
    describeCronRemote(cron);
    return () => {
      describeSeq.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone]);

  // Natural language → cron (Claude). Debounced; only after a real edit.
  useEffect(() => {
    if (!naturalTouched.current) return;
    const text = scheduleNatural.trim();
    if (!text) {
      setState(scheduleCron.trim() ? 'valid' : 'idle');
      setError(null);
      return;
    }
    setState('parsing');
    setError(null);
    const handle = setTimeout(async () => {
      try {
        const res = await fetch('/api/routines/parse-schedule', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, timezone }),
        });
        // Parser unavailable for THIS caller (anonymous rate limit, or a stale
        // deploy still auth-gating) — not the user's fault, and save-time
        // resolution will still apply the phrase. Defer, don't alarm.
        if (res.status === 429 || res.status === 401) {
          setDescription(null);
          setNextRun(null);
          setState('deferred');
          setError(null);
          return;
        }
        const data = await res.json();
        if (data.valid && data.cron) {
          // A fresh parse supersedes any in-flight describe — a slow stale
          // response must not overwrite this newer schedule.
          describeSeq.current++;
          onScheduleCronChange(data.cron);
          setDescription(data.description);
          setNextRun(typeof data.nextRun === 'string' ? data.nextRun : null);
          setState('valid');
          setError(null);
        } else {
          setNextRun(null);
          setState('invalid');
          setError(data.error || 'Couldn’t read that as a schedule.');
        }
      } catch {
        // Network failure — same contract as 429: the phrase is applied at
        // save; no need to block or alarm the user mid-typing.
        setDescription(null);
        setNextRun(null);
        setState('deferred');
        setError(null);
      }
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleNatural, timezone]);

  // Advanced cron edited directly → re-describe locally (no Claude). Debounced.
  useEffect(() => {
    if (!cronTouched.current) return;
    const cron = scheduleCron.trim();
    if (!cron) {
      setDescription(null);
      setNextRun(null);
      setState('idle');
      setError(null);
      return;
    }
    const handle = setTimeout(() => describeCronRemote(cron), 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleCron]);

  return (
    <div className="space-y-4">
      {/* Primary: natural language */}
      <div>
        <Label>When should this run?</Label>
        <Input
          value={scheduleNatural}
          onChange={(e) => {
            naturalTouched.current = true;
            onScheduleNaturalChange(e.target.value);
          }}
          placeholder="e.g., Every weekday at 9am"
        />

        {/* Live confirmation of what will actually fire */}
        <div className="mt-2 min-h-[1.25rem] text-sm">
          {state === 'parsing' && (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Working out the schedule…
            </span>
          )}
          {state === 'valid' && (
            <span className="flex flex-col gap-1">
              <span className="flex items-start gap-2 text-emerald-600 dark:text-emerald-400">
                <Check className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Runs {description ? description.toLowerCase() : 'on the set schedule'}
                  {timezone ? <span className="text-muted-foreground"> · {timezone}</span> : null}
                </span>
              </span>
              {nextRun && (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <CalendarClock className="h-4 w-4 shrink-0" />
                  <span>Next run: {formatNextRun(nextRun, timezone)}</span>
                </span>
              )}
            </span>
          )}
          {state === 'invalid' && (
            <span className="flex items-start gap-2 text-amber-600 dark:text-amber-500">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </span>
          )}
          {state === 'deferred' && (
            <span className="flex items-start gap-2 text-muted-foreground">
              <CalendarClock className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                We’ll set “{scheduleNatural.trim()}” exactly when you save — or use the advanced
                cron field below.
              </span>
            </span>
          )}
          {state === 'idle' && (
            <span className="flex items-center gap-2 text-muted-foreground">
              <CalendarClock className="h-4 w-4" /> Type when it should run — we’ll turn it into a schedule.
            </span>
          )}
        </div>
      </div>

      {/* Advanced: raw cron override */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger
          className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          type="button"
        >
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', advancedOpen && 'rotate-180')} />
          Advanced: cron expression
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          <Input
            value={scheduleCron}
            onChange={(e) => {
              cronTouched.current = true;
              onScheduleCronChange(e.target.value);
            }}
            placeholder="0 9 * * 1-5"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Auto-filled from your phrase above. Edit only if you need precise control — this is
            what the scheduler actually fires on.
          </p>
        </CollapsibleContent>
      </Collapsible>

      {/* Timezone */}
      <div>
        <Label>Timezone</Label>
        <div className="mt-1">
          <TimezoneSelect value={timezone} onChange={onTimezoneChange} />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Auto-detected from your browser. The schedule above is interpreted in this timezone.
        </p>
      </div>
    </div>
  );
}
